import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Logo from '../ui/Logo';
import MobileGlassMenu from './MobileGlassMenu';

const NAV_BREAK = '1180px';

const glass = `
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(24px) saturate(200%);
  -webkit-backdrop-filter: blur(24px) saturate(200%);
  border: 1px solid rgba(255, 255, 255, 0.85);
`;

const HeaderRoot = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 10px var(--container-padding) 0;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const NavBar = styled.nav`
  max-width: var(--container-width);
  margin: 0 auto;
  min-height: var(--nav-height);
  padding: 12px 18px 12px 22px;
  border-radius: 24px;
  transition: all 0.4s cubic-bezier(0.22, 1, 0.36, 1);

  ${(p) =>
    p.$scrolled
      ? `
    ${glass}
    background: rgba(255, 255, 255, 0.85);
    box-shadow: 0 12px 48px rgba(7, 17, 31, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.95);
  `
      : `
    ${glass}
    background: rgba(255, 255, 255, 0.55);
    box-shadow: 0 6px 32px rgba(7, 17, 31, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.9);
  `}
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(10px, 2vw, 24px);
`;

const Brand = styled.div`
  flex: 0 0 auto;
  min-width: 0;
  z-index: 2;
  margin-right: 4px;

  @media (max-width: ${NAV_BREAK}) {
    flex: 1 1 auto;
    max-width: calc(100% - 60px);
    margin-right: 0;
  }
`;

const DesktopNavWrap = styled.div`
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  min-width: 0;
  padding: 6px;
  margin: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 2px 12px rgba(7, 17, 31, 0.04);

  @media (max-width: ${NAV_BREAK}) {
    display: none;
  }

  @media (max-width: 1320px) {
    margin: 0 8px;
  }
`;

const DesktopNav = styled.ul`
  display: flex;
  align-items: center;
  gap: 2px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const DesktopLink = styled(Link)`
  display: block;
  padding: 12px 18px;
  font-size: 0.9rem;

  @media (max-width: 1320px) {
    padding: 11px 14px;
    font-size: 0.85rem;
  }
  font-weight: 600;
  color: ${(p) => (p.$active ? '#fff' : '#1e293b')};
  text-decoration: none;
  border-radius: 999px;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  ${(p) =>
    p.$active &&
    `
    font-weight: 700;
    background: linear-gradient(135deg, rgba(14, 107, 60, 0.95) 0%, rgba(10, 90, 50, 0.98) 100%);
    box-shadow: 0 6px 20px rgba(14, 107, 60, 0.4);
  `}

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : '#0e6b3c')};
    background: ${(p) =>
      p.$active ? undefined : 'rgba(255, 255, 255, 0.75)'};
    transform: translateY(-1px);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const DesktopCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 13px 24px;
  border-radius: 999px;
  color: #fff;
  font-weight: 700;
  font-size: 0.875rem;
  font-family: var(--font-display);
  text-decoration: none;
  white-space: nowrap;
  background: linear-gradient(135deg, rgba(14, 107, 60, 0.95) 0%, rgba(10, 90, 50, 0.98) 100%);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 10px 32px rgba(14, 107, 60, 0.35);
  backdrop-filter: blur(12px);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 40px rgba(14, 107, 60, 0.45);
  }

  @media (max-width: ${NAV_BREAK}) {
    display: none;
  }

  @media (max-width: 1320px) {
    padding: 11px 18px;
    font-size: 0.8rem;
  }
`;

const MenuBtn = styled.button`
  display: none;
  position: relative;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  cursor: pointer;
  flex-shrink: 0;
  z-index: 2;
  box-shadow: 0 6px 20px rgba(7, 17, 31, 0.08);
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);

  @media (max-width: ${NAV_BREAK}) {
    display: flex;
  }

  ${(p) =>
    p.$open &&
    `
    background: rgba(14, 107, 60, 0.92);
    border-color: rgba(14, 107, 60, 0.95);
  `}

  span {
    position: absolute;
    width: 22px;
    height: 2.5px;
    background: ${(p) => (p.$open ? '#fff' : '#07111f')};
    border-radius: 3px;
    transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  }

  span:nth-child(1) {
    transform: ${(p) => (p.$open ? 'translateY(0) rotate(45deg)' : 'translateY(-7px)')};
  }
  span:nth-child(2) {
    opacity: ${(p) => (p.$open ? 0 : 1)};
  }
  span:nth-child(3) {
    transform: ${(p) => (p.$open ? 'translateY(0) rotate(-45deg)' : 'translateY(7px)')};
  }

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 8px 24px rgba(7, 17, 31, 0.12);
  }
`;

const NAV_ITEMS = [
  { path: '/', label: 'Home' },
  { path: '/services', label: 'Services' },
  { path: '/projects', label: 'Portfolio' },
  { path: '/about', label: 'About Us' },
  { path: '/pricing', label: 'Pricing' },
  { path: '/contact', label: 'Contact' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeMenu]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <HeaderRoot>
      <NavBar $scrolled={scrolled} aria-label="Main navigation">
        <NavInner>
          <Brand>
            <Logo size="bar" maxWidth="500px" />
          </Brand>

          <DesktopNavWrap>
            <DesktopNav>
              {NAV_ITEMS.map((item) => (
                <li key={item.path}>
                  <DesktopLink to={item.path} $active={isActive(item.path)}>
                    {item.label}
                  </DesktopLink>
                </li>
              ))}
            </DesktopNav>
          </DesktopNavWrap>

          <Actions>
            <DesktopCTA to="/contact">
              Get Free Consultation
              <i className="fas fa-arrow-right" aria-hidden />
            </DesktopCTA>
            <MenuBtn
              type="button"
              $open={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
            >
              <span />
              <span />
              <span />
            </MenuBtn>
          </Actions>
        </NavInner>
      </NavBar>

      <MobileGlassMenu
        open={menuOpen}
        onClose={closeMenu}
        items={NAV_ITEMS}
        isActive={isActive}
      />
    </HeaderRoot>
  );
};

export default Header;
